import logger from "../utils/logger";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  authService,
  isDefinitiveAuthError,
  isAuthStorageKey,
  IDENTITY_CHANGED_EVENT,
  EXTERNAL_LOGOUT_EVENT,
} from "../utils/auth";
import SessionChangedNotice from "../components/auth/SessionChangedNotice";
import { useInactivityTimeout } from "../hooks/useInactivityTimeout";
import InactivityWarningModal from "../components/auth/InactivityWarningModal";
import { logAuthDiagnostic } from "../utils/blankPageDiagnostics";
import { subscribeAuthSync, AUTH_SYNC_EVENTS } from "../utils/authSyncChannel";

/**
 * SessionContext — the reactive source of the current user.
 *
 * Before this existed, `usePermissions` read localStorage synchronously with no
 * subscription, so nothing could change a user's access without a page reload. Solution
 * Access must be changeable by an admin at any time and take effect without a re-login,
 * which is why:
 *
 *   - solutions are NOT a JWT claim. They come from GET /api/auth/me, which reads
 *     org_users on every request. A token claim would require re-issuing the token, and
 *     "no logout/login" would be impossible.
 *   - this context is the single place the user object lives, so a refresh() re-renders
 *     every consumer (sidebar, route guards, members page) at once.
 *
 * Refresh triggers, cheapest first:
 *   1. mount
 *   2. navigation to a route while the session is stale (see `sessionIsStale` below)
 *   3. window focus            (the user comes back to the tab)
 *   4. window storage          (another tab logged in/out)
 *   5. `solution-denied` event (a 403 SOLUTION_DENIED landed; see utils/api.js)
 *   6. `synexos_auth_sync`     (BroadcastChannel cross-tab notification)
 */

/**
 * True when the cached user cannot answer a Solution Access question.
 */
const sessionIsStale = (user) => !user || !Array.isArray(user.solutions);
const SessionContext = createContext(null);

export const SOLUTION_DENIED_EVENT = "synexos:solution-denied";
export const RBAC_EVENT = "synexos:rbac-event";

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

async function attemptRefreshTokenWithRetry(maxAttempts = 3) {
  let lastErr = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await authService.refreshToken();
    } catch (err) {
      lastErr = err;
      if (isDefinitiveAuthError(err)) {
        throw err; // Stop immediately on definitive rejection
      }
      if (attempt < maxAttempts) {
        logAuthDiagnostic("SessionContext.refresh_retry_delay", {
          attempt,
          delayMs: attempt * 800,
          error: err?.message || String(err),
        });
        await sleep(attempt * 800);
      }
    }
  }
  throw lastErr;
}

export function SessionProvider({ children }) {
  // Only initialize user if actively authenticated in memory; never treat unverified localStorage as current identity.
  const [user, setUser] = useState(() => {
    return authService.isAuthenticated() ? authService.getUser() : null;
  });
  const [loading, setLoading] = useState(() => {
    return !authService.isAuthenticated() && authService.hasRefreshToken();
  });
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  // Set when another tab signed in as a different user/organisation. This tab is frozen
  // (authService hands out no credentials) until the user explicitly reloads it.
  const [identityChange, setIdentityChange] = useState(() => authService.getIdentityChange());
  const inFlight = useRef(null);
  const reqSeqRef = useRef(0);
  const pendingRefreshRef = useRef(false);
  const location = useLocation();

  // Automatic 15-minute inactivity auto-logout (with shared cross-tab tracking, active meeting lease exemption, and 60s warning modal)
  const { isWarningOpen, countdown, stayLoggedIn } = useInactivityTimeout({
    user,
    onLogout: () => {
      logAuthDiagnostic("inactivity_onLogout_setUser_null");
      authService.stopProactiveRefresh();
      setUser(null);
    }
  });

  const refresh = useCallback(async (triggerSource = "direct") => {
    const currentToken = authService.getToken();
    const isAuth = authService.isAuthenticated();
    const isExpiringSoon = authService.isTokenExpiringSoon(currentToken);
    const hasRefresh = authService.hasRefreshToken();

    logAuthDiagnostic("SessionContext.refresh_invoked", {
      triggerSource,
      isAuthenticated: isAuth,
      isExpiringSoon,
      hasRefreshToken: hasRefresh,
      currentUserExists: Boolean(authService.getUser()),
    });

    if (!isAuth || (isExpiringSoon && hasRefresh)) {
      if (hasRefresh) {
        try {
          logAuthDiagnostic("SessionContext.refresh_attempting_token_refresh", { triggerSource });
          await attemptRefreshTokenWithRetry(3);
          setIsOffline(false);
        } catch (err) {
          if (isDefinitiveAuthError(err)) {
            logAuthDiagnostic("SessionContext.refresh_definitive_auth_failure_clearing_user", {
              error: err?.message || String(err),
            });
            authService.logout();
            setUser(null);
            setLoading(false);
            setIsOffline(false);
            return null;
          } else {
            // Transient error (network lag, sleep wake-up, offline)
            logAuthDiagnostic("SessionContext.refresh_transient_failure_preserving_user", {
              error: err?.message || String(err),
            });
            if (!isAuth) {
              // Identity has not been validated: withhold profile, resolve loading, flag offline
              setUser(null);
              setLoading(false);
              setIsOffline(true);
              return null;
            }
          }
        }
      } else if (!isAuth) {
        logAuthDiagnostic("SessionContext.refresh_no_refresh_token_clearing_user");
        authService.stopProactiveRefresh();
        setUser(null);
        setLoading(false);
        setIsOffline(false);
        return null;
      }
    }
    // Collapse concurrent refreshes and mark pending if another is in-flight.
    if (inFlight.current) {
      pendingRefreshRef.current = true;
      return inFlight.current;
    }

    const currentSeq = ++reqSeqRef.current;
    const hasExistingUser = Boolean(user || authService.getUser());
    if (hasExistingUser) {
      setIsRevalidating(true);
    } else {
      setLoading(true);
    }

    inFlight.current = authService
      .getMe()
      .then(() => {
        // Drop response if a newer request was dispatched while this was pending
        if (currentSeq < reqSeqRef.current) {
          return null;
        }
        // getMe() has already written localStorage; re-read so `orgRole` is injected.
        const fresh = authService.getUser();
        logAuthDiagnostic("SessionContext.refresh_getMe_success", {
          freshUserLoaded: Boolean(fresh),
          solutionsCount: fresh?.solutions?.length ?? 0,
        });
        setUser(fresh);
        authService.startProactiveRefresh();
        return fresh;
      })
      .catch((err) => {
        // Authoritative rejection: check if account was deactivated or revoked
        const status = err.response?.status;
        const errDetail = err.response?.data?.detail;
        const detailCode = typeof errDetail === "object" ? errDetail?.detail : errDetail;

        if (
          status === 401 ||
          (status === 403 && (detailCode === "ACCOUNT_DEACTIVATED" || detailCode === "NO_MEMBERSHIP" || isDefinitiveAuthError(err)))
        ) {
          logAuthDiagnostic("SessionContext.refresh_authoritative_rejection_clearing_session", {
            status,
            detailCode,
          });
          authService.logout();
          setUser(null);
          if (typeof window !== "undefined") {
            window.location.replace("/auth/login");
          }
          return null;
        }

        // A failed refresh must not clear the session: a transient network error would
        // otherwise log the user out of the UI while their token is still valid.
        logger.error("[Session] refresh failed", err?.message || "Unknown error");
        logAuthDiagnostic("SessionContext.refresh_getMe_failed", {
          error: err?.message || String(err),
        });
        return null;
      })
      .finally(() => {
        if (currentSeq === reqSeqRef.current) {
          setLoading(false);
          setIsRevalidating(false);
          inFlight.current = null;
          if (pendingRefreshRef.current) {
            pendingRefreshRef.current = false;
            // Schedule one subsequent refresh to capture latest queued state
            setTimeout(() => refresh("queued_rbac_update"), 50);
          }
        }
      });

    return inFlight.current;
  }, []);

  // Re-check on every navigation.
  const healedFor = useRef(null);
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      if (authService.hasRefreshToken() && Boolean(authService.getUser())) {
        refresh("nav_unauth_has_refresh");
        return;
      }
      healedFor.current = null;
      if (user) {
        logAuthDiagnostic("SessionContext.nav_unauth_no_refresh_setUser_null");
        authService.stopProactiveRefresh();
        setUser(null);
      }
      return;
    }
    if (!sessionIsStale(user)) return;
    if (healedFor.current === location.pathname) return;
    healedFor.current = location.pathname;
    refresh("nav_stale_session");
  }, [location.pathname, user, refresh]);

  useEffect(() => {
    // Start proactive refresh on provider mount if authenticated
    if (authService.isAuthenticated() && authService.hasRefreshToken()) {
      authService.startProactiveRefresh();
    }

    if (authService.isAuthenticated() || Boolean(authService.getUser())) {
      refresh("mount");
    }

    const onFocus = () => {
      if (authService.isAuthenticated() || Boolean(authService.getUser())) {
        refresh("window_focus");
      }
    };
    const onVisibilityChange = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        if (authService.isAuthenticated() || Boolean(authService.getUser())) {
          refresh("visibility_change");
        }
      }
    };
    const onStorage = (e) => {
      // Only authentication keys matter here. Unrelated writes (activity heartbeat, meeting
      // leases, calendar state…) previously re-rendered every consumer in every tab.
      if (e && !isAuthStorageKey(e.key)) return;
      logAuthDiagnostic("SessionContext.window_storage_event");
      if (authService.isAuthenticated()) {
        const currentUser = authService.getUser();
        setUser(currentUser);
        setLoading(false);
        if (authService.hasRefreshToken()) {
          authService.startProactiveRefresh();
        }
      } else if (!authService.hasRefreshToken()) {
        authService.stopProactiveRefresh();
        setUser(null);
        setLoading(false);
      }
    };
    const onSolutionDenied = () => refresh("solution_denied");

    const onRbacEvent = (e) => {
      const eventData = e?.detail;
      if (!eventData) return;

      const current = authService.getUser();
      if (!current) return;

      const myUserId = String(current.user_id || current.id || current._id || "").trim();
      const myOrgId = String(current.org_id || "").trim();

      // Tenant check: ignore if event is for a different organization
      if (eventData.org_id && myOrgId && eventData.org_id !== myOrgId) {
        return;
      }

      // Check whether this event affects the current authenticated user:
      const targetUserId = eventData.target_user_id ? String(eventData.target_user_id).trim() : null;
      const affectedUserIds = Array.isArray(eventData.affected_user_ids)
        ? eventData.affected_user_ids.map((id) => String(id).trim())
        : [];

      const isTargetUser = Boolean(targetUserId && (targetUserId === myUserId || targetUserId === String(current.id)));
      const isAffectedUser = affectedUserIds.includes(myUserId) || (current.id && affectedUserIds.includes(String(current.id)));

      const myRoleId = String(current.hierarchical_role_id || current.role_id || current.role || "").toLowerCase().trim();
      const eventRoleId = String(eventData.role_id || "").toLowerCase().trim();
      const isMyRole = Boolean(eventRoleId && (myRoleId === eventRoleId || (current.role && current.role.toLowerCase() === eventRoleId)));

      // Check for affected roles list if provided (e.g. downward cascade pruning)
      const affectedRoles = Array.isArray(eventData.payload?.affected_roles)
        ? eventData.payload.affected_roles.map((r) => String(r).toLowerCase().trim())
        : [];
      const isRoleInAffectedTree = affectedRoles.includes(myRoleId);

      const isHierarchyChange = eventData.action === "role_hierarchy_updated";
      const isSolutionsUpdate = eventData.action === "solutions_updated" && (isTargetUser || isAffectedUser);
      const isDeactivatedOrRemoved = isTargetUser && (eventData.action === "user_deactivated" || eventData.action === "user_removed");

      const affectsMe = isTargetUser || isAffectedUser || isMyRole || isRoleInAffectedTree || isHierarchyChange || isSolutionsUpdate || isDeactivatedOrRemoved;

      if (affectsMe) {
        logAuthDiagnostic("SessionContext.rbac_event_refresh_invoked", {
          action: eventData.action,
          targetUserId,
          roleId: eventData.role_id,
        });

        refresh("rbac_event");
      }
    };

    const onOnline = () => {
      logAuthDiagnostic("SessionContext.window_online_event");
      setIsOffline(false);
      if (!authService.isAuthenticated() && authService.hasRefreshToken()) {
        refresh("network_online");
      }
    };
    const onOffline = () => {
      logAuthDiagnostic("SessionContext.window_offline_event");
      setIsOffline(true);
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("focus", onFocus);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }
    window.addEventListener("storage", onStorage);
    window.addEventListener(SOLUTION_DENIED_EVENT, onSolutionDenied);
    window.addEventListener(RBAC_EVENT, onRbacEvent);

    // authService detected (while reconciling with shared storage) that another tab ended
    // the session or switched identity. Drop the reactive user so route guards redirect.
    const onExternalLogout = () => {
      authService.stopProactiveRefresh();
      setUser(null);
      setLoading(false);
      setIsOffline(false);
    };
    const onIdentityChanged = (e) => {
      setIdentityChange(e?.detail || authService.getIdentityChange());
      setUser(null);
      setLoading(false);
    };
    window.addEventListener(EXTERNAL_LOGOUT_EVENT, onExternalLogout);
    window.addEventListener(IDENTITY_CHANGED_EVENT, onIdentityChanged);

    // Cross-tab BroadcastChannel subscriber
    const unsubscribeAuthSync = subscribeAuthSync((event) => {
      if (event.type === AUTH_SYNC_EVENTS.AUTH_TOKEN_UPDATED || event.type === AUTH_SYNC_EVENTS.AUTH_LOGIN) {
        logAuthDiagnostic("SessionContext.broadcast_auth_sync_updated");
        const freshUser = authService.getUser();
        setUser(freshUser);
        setLoading(false);
        setIsOffline(false);
        if (authService.isAuthenticated() && authService.hasRefreshToken()) {
          authService.startProactiveRefresh();
        }
      } else if (event.type === AUTH_SYNC_EVENTS.AUTH_LOGOUT) {
        logAuthDiagnostic("SessionContext.broadcast_auth_logout");
        authService.stopProactiveRefresh();
        setUser(null);
        setLoading(false);
        setIsOffline(false);
      }
    });

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("focus", onFocus);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SOLUTION_DENIED_EVENT, onSolutionDenied);
      window.removeEventListener(RBAC_EVENT, onRbacEvent);
      window.removeEventListener(EXTERNAL_LOGOUT_EVENT, onExternalLogout);
      window.removeEventListener(IDENTITY_CHANGED_EVENT, onIdentityChanged);
      unsubscribeAuthSync();
    };
  }, [refresh]);

  const value = useMemo(
    () => ({ user, loading, isRevalidating, isOffline, refresh, identityChange }),
    [user, loading, isRevalidating, isOffline, refresh, identityChange]
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
      {identityChange && <SessionChangedNotice change={identityChange} />}
      {isOffline && !user && (
        <div
          role="alert"
          aria-live="polite"
          data-testid="session-offline-banner"
          className="fixed bottom-4 right-4 z-50 max-w-md bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-4 shadow-xl flex items-center justify-between gap-3 animate-fade-in"
        >
          <div className="flex items-center gap-2.5">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.828m2.829 2.828L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.828m-4.243 4.243a9 9 0 01-12.728 0m0 0l2.828-2.829m-2.828 2.829L3 21m2.828-5.657a5 5 0 010-7.072m0 0l2.829 2.829m4.242-4.242a1 1 0 00-1.414 0L3 14" />
            </svg>
            <div>
              <p className="text-xs font-semibold">Connection Interrupted</p>
              <p className="text-xs text-amber-700">Unable to verify session. Will retry when connection returns.</p>
            </div>
          </div>
          <button
            onClick={() => refresh("manual_retry")}
            className="px-3 py-1.5 text-xs font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors shadow-sm flex-shrink-0"
          >
            Retry
          </button>
        </div>
      )}
      {user && (
        <InactivityWarningModal
          isOpen={isWarningOpen}
          countdown={countdown}
          onStayLoggedIn={stayLoggedIn}
        />
      )}
    </SessionContext.Provider>
  );
}

/**
 * Read the session. Returns null when called outside a SessionProvider — consumers fall
 * back to a direct localStorage read so components rendered outside the provider
 * (auth pages, the client portal) keep working.
 */
export function useSession() {
  return useContext(SessionContext);
}

export default SessionContext;
